import { Modal } from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { getMe, startReferralProbe } from "../api/me";
import { useSurveyForm } from "../hooks/useSurveyForm";
import { redirectTopLevel } from "../utils/redirect";
import { referralProbeContext } from "../utils/shopifyEntryContext";
import { SurveyQuestionFields } from "./SurveyQuestionFields";

import { t } from "../lib/i18n";

/**
 * 初回起動アンケート（install attribution フォールバック）。
 *
 * `/api/me` の needs_survey が true（ref/waitlist で流入元が取れなかった
 * organic/App Store 店で未回答）のときに表示する。×/ESC/背景で「後で」閉じられる
 * （dismissed のローカル state のみ・needs_survey は true のまま）ので、答えるまで
 * アプリを開き直すたびに再表示される＝閉じられない強制モーダルにはしない（審査/UX
 * 対策）。送信で ["me"] を invalidate → needs_survey が false になり恒久的に消える。
 * needs_referral_probe が true の場合は、ref 回収をアンケートより優先し、probe の
 * トップレベル遷移だけを1マウントにつき1回開始する。
 *
 * 課金直前の**飛ばせない**版は [[BillingSurveyGateModal]]（Plans 画面専用）。
 */
export function InstallSurveyModal() {
  const { data } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const needsSurvey = data?.needs_survey === true;
  // 新SPA→旧backendの rollout skew でも紹介帰属を落とさない。me 取得後は
  // false の明示だけを通常導線として許可し、flag 欠落は probe 必須へ倒す。
  const needsReferralProbe =
    data !== undefined && data.needs_referral_probe !== false;
  const referralProbeStarted = useRef(false);
  // ×/ESC/背景で「後で」閉じるための一時 state（次回ロードで再表示される）。
  const [dismissed, setDismissed] = useState(false);

  const form = useSurveyForm({
    enabled: needsSurvey && !dismissed && !needsReferralProbe,
  });

  useEffect(() => {
    if (!needsReferralProbe || referralProbeStarted.current) return;
    const context = referralProbeContext();
    // host を推測して送ると別の Shopify Admin 文脈へ戻す危険がある。
    // 正規 entry からの次回マウントまで、POST もアンケート表示もしない。
    if (!context) return;
    referralProbeStarted.current = true;

    void startReferralProbe(context)
      .then(({ redirect_url }) => redirectTopLevel(redirect_url))
      // `/api/me` が probe 必須と言っている間は、失敗時もアンケートへ
      // フォールバックしない。次のマウントでだけ安全に再試行する。
      .catch(() => undefined);
  }, [needsReferralProbe]);

  if (needsReferralProbe) return null;

  return (
    <Modal
      open={needsSurvey && !dismissed}
      onClose={() => setDismissed(true)}
      title={t("UreyukiBox をどこで知りましたか？")}
      primaryAction={{
        content: t("送信"),
        disabled: !form.canSubmit,
        loading: form.isPending,
        onAction: form.submit,
      }}
    >
      <Modal.Section>
        <SurveyQuestionFields
          source={form.source}
          onSourceChange={form.setSource}
          showsPartnerPicker={form.showsPartnerPicker}
          showsOtherSourceInput={form.showsOtherSourceInput}
          isOtherPartner={form.isOtherPartner}
          partner={form.partner}
          onPartnerChange={form.setPartner}
          partnerOptions={form.partnerOptions}
          otherReferrer={form.otherReferrer}
          onOtherReferrerChange={form.setOtherReferrer}
          isError={form.isError}
        />
      </Modal.Section>
    </Modal>
  );
}
